import type { ReactElement } from 'react';
import { cloneElement } from 'react';

import { getZendeskHelpCentreUrl, ZENDESK_TABS } from '@skyscanner/zendesk-url';
import { BpkAccordion } from '@skyscanner-internal/backpack-web/bpk-component-accordion';
import BpkLink from '@skyscanner-internal/backpack-web/bpk-component-link';
import Copyright from '@web-platform/global-components-copyright';
import StringsPropVerification from '@web-platform/global-components-higher-order-components/StringsPropVerification';
import { fillPlaceholders } from '@web-platform/global-components-utils/stringUtils';
import { trackEventFn as defaultTrackEventFn } from '@web-platform/global-components-utils/tracking';

import CultureSelectorButton from './CultureSelectorButton/CultureSelectorButton';
import CultureText from './CultureText';
import FooterGroup from './FooterGroup';
import MobileAppButton from './MobileAppButton';
import { mbdTrackingParams } from './helpers/mbd';

import type { ProductVertical } from './constants';
import type { CultureSelectorFooterEmbeddedProps } from './types';
import type { OnCultureSelectorButtonClick } from '@web-platform/global-components-culture-selector/helpers/useCultureSelectorData';
import type { Logger, Nullable } from '@web-platform/global-components-types/utils';
import type { TrackingEventFn } from '@web-platform/global-components-utils/tracking';

import STYLES from './Footer.module.scss';

const stringsKeys = [
  'help',
  'privacySettings',
  'footerLogin',
  'footerCopyright',
  'close',
  'expand',
  'getMobileApp',
] as const;

/**
 * @public
 * @interface
 * @property close - Text for closing certain UI elements.
 * @property expand - Text for expanding certain UI elements.
 * @property footerCopyright - Copyright text for the footer.
 * @property footerLogin - Text for "Log in" link in the footer.
 * @property help - Text for "Help" link.
 * @property privacySettings - Text for "Privacy Settings" link.
 */
export type Strings = {
  [key in (typeof stringsKeys)[number]]: string;
};
/**
 * @public
 */
export type Link = {
  id: string;
  href: Nullable<string>;
  rel?: string;
  target?: string;
  text: Nullable<string>;
  placeId?: string;
  links?: Link[];
  response?: string;
};
/**
 * @public
 */
export type LinkGroup = {
  links: Link[];
  id: string;
  heading: string;
};
/**
 * @public
 */
export type FooterLinks = Array<Nullable<LinkGroup>>;
/**
 * @public
 */
export type CultureData = {
  /**
   * Pre-selected market in the modal.
   * @defaultValue 'uk'
   */
  country: string;
  currencyData?: {
    symbol: string;
    currency: string;
    name: string;
  };
  /**
   * Pre-selected locale in the modal.
   * @defaultValue 'en-gb'
   */
  languageShort: string;
  /**
   * Pre-selected locale in the modal (Must be in sync with locale).
   * @defaultValue 'English (UK)'
   */
  localeNativeShortName?: string;
  /**
   * Pre-selected market in the modal (Must be in sync with market).
   * @defaultValue 'United Kingdom'
   */
  marketTranslatedName?: string;
};

export type AppDownLoadLinks = {
  trackingLink: string;
  appStoreLink: string;
  googlePlayLink: string;
};

type DeviceInfo = {
  osName: string;
};
/**
 * @public
 */
export interface FooterProps {
  /**
   * A BpkLink component for user links.
   */
  userLinkSlot?: ReactElement<{ className?: string }>;
  /**
   * Temporary, used by Banana for integration of culture-selector.
   */
  cultureSelectorButtonSlot?: ReactElement;
  /**
   * Collection of links to be used. Uses provided Data Helper `getFooterLinks`.
   */
  footerLinks: FooterLinks;
  /**
   * Product vertical to determine certain behavior.
   */
  productVertical?: ProductVertical;
  /**
   * Object detailing the cultural context.
   */
  cultureData: CultureData;
  /**
   * Translated strings for the component.
   */
  strings: Strings;
  /**
   * Logger object.
   */
  logger: Logger;
  /**
   *  A boolean determining if the environment is production.
   */
  isProductionEnv: boolean;
  /**
   * User's device information can be obtained from RequestContext.
   */
  deviceInfo?: DeviceInfo;
  /**
   * The link to redirect to the app store,
   * can be obtained by 'footerAppDownloadTrackingLink', 'googlePlayLink', 'appStoreLink' from Configuration service
   */
  appDownLoadLinks?: AppDownLoadLinks;
  /**
   * The pageType of page that the footer is being rendered on.
   */
  pageType?: string;
  /**
   * A function to track events. If not provided the `defaultTrackEventFn` will be used.
   */
  trackEventFn?: TrackingEventFn;
  /**
   * A boolean flag indicating if the data is currently being fetched for culture selector
   */
  isFetchingCultureSelectorData?: boolean;
  /**
   * Props for use cases when CultureSelector modal is used inside Footer
   */
  cultureSelectorProps?: CultureSelectorFooterEmbeddedProps;
  /**
   * A callback function used to handle the lazy loading of the culture selector on a
   * button click event
   */
  onCultureSelectorButtonClick?: OnCultureSelectorButtonClick;
}

const SEO_CONTENT_ID = 'footerSeoLinks';

/**
 * The `Footer` component displays useful links and adapts its context based on the vertical.
 *
 * @public
 * @example
 * ```tsx
 * import Footer from '@skyscanner-internal/global-components/footer';
 * import { requestContextParameters } from 'request-context-skyscanner';
 * import {
 *   fetchCultureSelectorData,
 *   useCultureSelectorData,
 * } from '@skyscanner-internal/global-components/culture-selector/helpers';

 * const footerLinks = await getFooterLinks(
 *  allFooterStrings[locale],
 *  allFooterFlagStrings,
 *  allMarkets.Items,
 *  currentMarket,
 *  locale,
 *  'flights',
 *  getUrl,
 *  travelApi.getEntitiesBySkyscannerCode.bind(travelApi),
 *  currentDomain,
 * );
 *
 * const cultureData = {
 *   country: 'UK',
 *   languageShort: 'en-GB',
 *   currencyData: {
 *     symbol: '£',
 *     currency: 'GBP',
 *     name: 'British Pound'
 *   }
 *   localeNativeShortName: 'English (UK)'
 *   marketTranslatedName: 'United Kingdom'
 * };
 *
 * const strings = {
 *   close: 'Close @@text@@ list',
 *   expand: 'Expand @@text@@ list',
 *   footerCopyright: '© Skyscanner Ltd 2002 – @@currentYear@@',
 *   footerLogin: 'Log in',
 *   help: 'Help',
 *   privacySettings: 'Privacy Settings'
 * };
 *
 * const device = await req.requestContext.getParameter(
 *   requestContextParameters.DEVICE,
 * );
 *
 * const appDownLoadLinks = {
 *   trackingLink: configServiceModel.getStringValue('footerAppDownloadTrackingLink'),
 *   appStoreLink: configServiceModel.getStringValue('appStoreLink),
 *   googlePlayLink: configServiceModel.getStringValue('googlePlayLink'),
 * };
 *
 * const {
 *   currencies,
 *   enMarkets,
 *   isFetchingCultureSelectorData,
 *   locales,
 *   markets,
 *   onCultureSelectorButtonClick,
 * } = useCultureSelectorData(fetchCultureSelectorData, locale, false);
 * cultureSelectorStrings: {
 *   titleLabel: 'Regional settings',
 *   marketLabel: 'Country / Region',
 *   currencyLabel: 'Currency',
 *   localeLabel: 'Language',
 *   cancelLabel: 'Cancel',
 *   closeLabel: 'Close',
 *   saveLabel: 'Save',
 *   countryRegionLabel: 'Country / Region',
 *   currencyFormatLabel: '{{currency_code}} - {{currency_symbol}}',
 *   selectCurrencyLabel: 'Select your currency',
 *   selectLocaleLabel: 'Select your language',
 *   popularLanguagesLabel: 'Popular languages',
 *   allLanguagesLabel: 'All languages',
 *   selectMarketLabel: 'Select your country',
 *   switchToEnglishLabel: 'Switch to English',
 *   popularCurrenciesLabel: 'Popular currencies',
 *   otherCurrenciesLabel: 'Other currencies',
 *   marketExplanationLabel:
 *          'Selecting the country you’re in will give you local deals and information.',
 * }
 * const Layout = () => {
 *   return <Footer
 *       appDownLoadLinks={appDownLoadLinks}
 *       footerLinks={footerLinks}
 *       productVertical="flights"
 *       cultureData={cultureData}
 *       deviceInfo={device}
 *       strings={strings}
 *       logger={logger}
 *       isProductionEnv={true}
 *       cultureSelectorProps={{
 *          currencies,
 *          locales,
 *          markets,
 *          enMarkets,
 *          isProductionEnv: false,
 *          strings: cultureSelectorStrings,
 *          enStrings: cultureSelectorStrings,
 *       }}
 *       isFetching={isFetchingCultureSelectorData}
 *       onCultureSelectorButtonClick={onCultureSelectorButtonClick}
 *     />
 * }
 * ```
 */
const Footer = ({
  appDownLoadLinks,
  cultureData,
  cultureSelectorButtonSlot,
  cultureSelectorProps,
  deviceInfo,
  footerLinks,
  isFetchingCultureSelectorData,
  isProductionEnv = false,
  logger,
  onCultureSelectorButtonClick = () => Promise.resolve(),
  pageType = 'unknown',
  productVertical = 'flights',
  strings,
  trackEventFn = defaultTrackEventFn,
  userLinkSlot,
}: FooterProps): ReactElement => {
  const { close, expand, footerCopyright, footerLogin, getMobileApp, help, privacySettings } =
    strings;
  let styledUserLinkSlot;
  if (userLinkSlot) {
    // We clone and apply the same className prop to the userLinkSlot prop that is passed in.
    // We expect the userLinkSlot prop to be a BpkLink component & this ensures that the styling is consistent with our other standard links
    styledUserLinkSlot = cloneElement(userLinkSlot, { className: STYLES.footer__link });
  }

  // If deviceInfo.osName, appDownLoadLinks.trackingLink and getMobileApp don't exist, hide the app download button
  const showAppDownloadButton = !!(deviceInfo?.osName && getMobileApp && appDownLoadLinks);

  const cultureClassNames = [STYLES.footer__culture];
  if (cultureSelectorButtonSlot || cultureSelectorProps) {
    // class name used for culture selector button, either users pass a whole
    // cultureSelectorButton component or use cultureSelectorButton in footer component
    cultureClassNames.push(STYLES['footer__culture-compress']);
  } else if (showAppDownloadButton) {
    cultureClassNames.push(STYLES['footer__culture-text']);
  }

  const cultureSelectorButton = cultureSelectorProps ? (
    <CultureSelectorButton
      cultureData={cultureData}
      cultureSelectorProps={cultureSelectorProps}
      isFetching={isFetchingCultureSelectorData}
      logger={logger}
      onCultureSelectorButtonClick={onCultureSelectorButtonClick}
      osName={deviceInfo?.osName}
      pageType={pageType}
      trackEventFn={trackEventFn}
      vertical={productVertical}
    />
  ) : undefined;
  const cultureSlot = cultureSelectorButtonSlot || cultureSelectorButton || (
    <CultureText cultureData={cultureData} />
  );

  const productVerticalZendeskMap = {
    'car-hire': ZENDESK_TABS.CARHIRE,
    flights: ZENDESK_TABS.FLIGHTS,
    hotels: ZENDESK_TABS.HOTELS,
    package: ZENDESK_TABS.PACKAGES,
  };

  const hrefLink = getZendeskHelpCentreUrl(
    productVerticalZendeskMap[productVertical],
    cultureData.languageShort,
    cultureData.country,
    // Workaround to ensure that currencyData doesn't block help from being generated
    cultureData.currencyData ? cultureData.currencyData.currency : 'GBP',
  );

  return (
    <StringsPropVerification
      stringsKeys={stringsKeys}
      componentName="Footer"
      strings={strings}
      logger={logger}
      isProductionEnv={isProductionEnv}
    >
      <footer
        className={STYLES.footer}
        data-tracking-element-id="footer"
        data-tracking-common-params={mbdTrackingParams('footer')}
      >
        <div className={STYLES['footer__link-container']}>
          <div className={STYLES['footer__button-group']}>
            {showAppDownloadButton ? (
              <MobileAppButton
                appDownLoadLinks={appDownLoadLinks}
                osName={deviceInfo?.osName}
                pageType={pageType}
                vertical={productVertical}
                cultureData={cultureData}
                strings={{ getMobileApp }}
                logger={logger}
                trackEventFn={trackEventFn}
              />
            ) : null}
            <div className={cultureClassNames.join(' ')}>{cultureSlot}</div>
          </div>
          <div className={STYLES['footer__user-links']}>
            <BpkLink
              href={hrefLink}
              alternate
              className={STYLES.footer__link}
              implicit
              data-tracking-element-id="footer_help_link"
              data-tracking-common-params={mbdTrackingParams('footer_help_link')}
            >
              {fillPlaceholders(help, {})}
            </BpkLink>
            {styledUserLinkSlot}
            <BpkLink
              href="/privacy-settings"
              alternate
              className={STYLES.footer__link}
              implicit
              data-tracking-element-id="footer_privacy_settings_link"
              data-tracking-common-params={mbdTrackingParams('footer_privacy_settings_link')}
            >
              {fillPlaceholders(privacySettings, {})}
            </BpkLink>
            <BpkLink
              href="/profile"
              alternate
              className={STYLES.footer__link}
              implicit
              data-tracking-element-id="footer_login_link"
              data-tracking-common-params={mbdTrackingParams('footer_login_link')}
            >
              {fillPlaceholders(footerLogin, {})}
            </BpkLink>
          </div>
          <div className={STYLES['footer__user-links']}>
            {footerLinks
              .filter((group): group is LinkGroup => group !== null && group.id === 'user')
              .flatMap((group) =>
                group.links.map((item) => (
                  <BpkLink
                    href={item.href}
                    alternate
                    className={STYLES.footer__link}
                    key={item.id}
                    implicit
                    data-tracking-element-id={`footer_user_link_${item.id}`}
                    data-tracking-common-params={mbdTrackingParams('footer_user_link', {
                      link_id: item.id,
                    })}
                  >
                    {item.text}
                  </BpkLink>
                )),
              )}
          </div>
          <BpkAccordion
            data-testid={SEO_CONTENT_ID}
            className={STYLES['footer__seo-links']}
            divider={false}
            onDark
          >
            {footerLinks
              .filter(
                (group): group is LinkGroup =>
                  group !== null && group.id !== 'user' && group.links.length > 0,
              )
              .map((item) => (
                <FooterGroup {...item} key={`${item.id}-content`} strings={{ expand, close }} />
              ))}
          </BpkAccordion>
        </div>
        <Copyright
          isProductionEnv={isProductionEnv}
          logger={logger}
          strings={{ footerCopyright }}
        />
      </footer>
    </StringsPropVerification>
  );
};

export default Footer;
